import * as React from "react";
import classNames from "classnames";
import "./Section.scss";
import { Container, Text, Title, HeroBg } from "../";
import { Icon as IconType } from "@tiendanube/icons";
import { motion } from "framer-motion";
import { animationContainer, fadeUpItem } from "../../utils/constants";

type Props = {
  background?: boolean;
  children?: React.ReactNode;
  hero?: boolean;
  imageContent?: React.ReactNode;
  subtitle?: string;
  primary?: boolean;
  title: string;
  titleBold?: string;
  text?: string;
  cta?: InterfaceCta;
}

export interface InterfaceCta {
  icon: IconType;
  href: string;
  children: string;
}

type Ref = HTMLDivElement;

const Section = React.forwardRef<Ref, Props>(({
  background = false,
  children,
  hero = false,
  imageContent,
  subtitle,
  primary= false,
  title,
  titleBold,
  text,
  cta,
}, ref) => {
  const className = classNames("nimbus-web--section", {"nimbus-web--section--hero": hero}, {"nimbus-web--section--background": background}, {"nimbus-web--section--primary": primary});

  const isAnimationEnded = React.useRef<boolean>(false);
  
  const isVisible = React.useMemo(() => isAnimationEnded.current ? "none" : "visible", [isAnimationEnded.current]);

  const MotionTitle = motion(Title);
  const MotionText = motion(Text);

  return (
    <motion.div ref={ref} className={className} initial="hidden" animate={isVisible} variants={animationContainer} onAnimationComplete={() => { isAnimationEnded.current = true; }}>
      {hero &&
        <HeroBg />
      }
      <Container>
        <Container.Column width="one-third">
          <MotionTitle variants={fadeUpItem} size={hero ? "h1" : "h2"}>{title}{titleBold && (<span className="bold"> {titleBold}</span>)}</MotionTitle>
          {subtitle &&
            <MotionText variants={fadeUpItem} size="medium">{subtitle}</MotionText>
          }
          {text &&
            <MotionText variants={fadeUpItem} size="base">{text}</MotionText>
          }
          {cta &&
            <motion.a variants={fadeUpItem} className="nimbus-web--link" href={cta.href} target="_blank">
              <cta.icon size="medium" />
              {cta.children}
            </motion.a>
          }
        </Container.Column>
        {imageContent && (
          <Container.Column width="two-thirds">
            {imageContent}
          </Container.Column>
        )}
      </Container>
      {children &&
        <div className="nimbus-web--section__content">
          {children}
        </div>
      }
    </motion.div>
  );
});

export default Section;